import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Calendar, MessageSquare, Sparkles, Wrench, BarChart3, Shield, ArrowRight } from 'lucide-react';
import { navigateWithScroll } from '@/utils/navigation';

export default function Services() {
  const navigate = useNavigate();
  
  const handleEstimateClick = () => {
    navigateWithScroll(navigate, '/estimation');
  };

  const services = [
    {
      icon: TrendingUp,
      title: 'Optimisation des revenus',
      benefit: 'Maximisez vos gains sans effort',
      details: [
        'Pricing dynamique adapté au marché en temps réel',
        'Analyse concurrentielle et saisonnalité',
        'Stratégies de tarification avancées',
        'Ajustements quotidiens pour maximiser l\'occupation'
      ]
    },
    {
      icon: Calendar,
      title: 'Gestion complète',
      benefit: 'Zéro charge mentale',
      details: [
        'Gestion du calendrier et des réservations',
        'Check-in et check-out automatisés',
        'Coordination de tous les intervenants',
        'Gestion des annulations et modifications'
      ]
    },
    {
      icon: MessageSquare,
      title: 'Communication voyageurs',
      benefit: 'Disponibilité 7j/7, réactivité garantie',
      details: [
        'Réponses rapides aux demandes (< 1h)',
        'Support multilingue',
        'Guide d\'accueil personnalisé',
        'Gestion des avis et réputation'
      ]
    },
    {
      icon: Sparkles,
      title: 'Ménage professionnel',
      benefit: 'Qualité hôtelière assurée',
      details: [
        'Équipe qualifiée et vérifiée',
        'Contrôle qualité systématique',
        'Gestion du linge et des consommables',
        'Intervention entre chaque séjour'
      ]
    },
    {
      icon: Wrench,
      title: 'Maintenance et incidents',
      benefit: 'Intervention rapide, problèmes résolus',
      details: [
        'Réseau de prestataires qualifiés',
        'Intervention d\'urgence 24/7',
        'Suivi des réparations et entretien',
        'Gestion des sinistres et assurances'
      ]
    },
    {
      icon: BarChart3,
      title: 'Reporting et transparence',
      benefit: 'Vous savez toujours où vous en êtes',
      details: [
        'Dashboard en temps réel',
        'Rapports mensuels détaillés',
        'Suivi des performances',
        'Paiements automatisés et réguliers'
      ]
    }
  ];

  return (
    <Layout>
      {/* Hero with Image */}
      <section 
        className="relative py-32 px-6 bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.6), rgba(43, 43, 43, 0.75)), url('https://images.unsplash.com/photo-1580842402762-6f5868c17412?w=1920&q=85')`,
        }}
        data-testid="services-hero"
      >
        <div className="max-w-4xl mx-auto text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Des services pensés pour <span className="text-[#8A9474]">votre réussite</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-100 max-w-2xl mx-auto">
            Chaque service est conçu pour maximiser vos revenus, réduire votre charge de travail et vous offrir une tranquillité totale.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => (
              <Card 
                key={service.title} 
                className="border-2 border-[#E5E2DB] hover:border-[#8A9474] transition-all shadow-sm hover:shadow-lg"
                data-testid={`service-detail-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-8">
                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[#E8EBE4] rounded-xl mb-6">
                    <service.icon className="h-7 w-7 text-[#8A9474]" />
                  </div>

                  {/* Title + Benefit */}
                  <h3 className="text-2xl font-semibold text-[#2B2B2B] mb-2">{service.title}</h3>
                  <p className="text-[#8A9474] font-medium mb-6 text-lg">{service.benefit}</p>

                  {/* Details */}
                  <ul className="space-y-3">
                    {service.details.map((detail) => (
                      <li key={detail} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-[#8A9474] rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-[#4A4A4A]">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Confiance Section with Image */}
      <section className="py-20 px-6 bg-[#F5F3EF]">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2B2B2B] mb-6">
                Confiez-nous votre bien en toute sérénité
              </h2>
              <p className="text-lg text-[#4A4A4A] mb-6 leading-relaxed">
                Depuis 8 ans, nous accompagnons des propriétaires comme vous dans la gestion de leur location saisonnière. Notre engagement : maximiser vos revenus tout en vous libérant de toutes les contraintes.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-[#8A9474] flex-shrink-0 mt-1" />
                  <span className="text-[#4A4A4A]"><strong>Assurance complète</strong> : votre bien est protégé contre les dommages</span>
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-[#8A9474] flex-shrink-0 mt-1" />
                  <span className="text-[#4A4A4A]"><strong>Contrat transparent</strong> : aucun frais caché, résiliation flexible</span>
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-[#8A9474] flex-shrink-0 mt-1" />
                  <span className="text-[#4A4A4A]"><strong>Paiements réguliers</strong> : virement mensuel automatique</span>
                </li>
              </ul>
              <Button
                onClick={handleEstimateClick}
                size="lg"
                className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold whitespace-normal sm:whitespace-nowrap"
                data-testid="services-cta-estimate"
              >
                Obtenir une estimation gratuite
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1586986422389-e500f0ed9f65?w=800&q=85" 
                alt="Couple heureux recevant les clés de leur bien immobilier géré par Homely Conciergerie"
                className="w-full h-auto md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 px-6 bg-[#F5F3EF]" data-testid="services-why-us">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#2B2B2B] mb-6">
            Pourquoi choisir Homely Conciergerie ?
          </h2>
          <p className="text-lg text-[#4A4A4A] mb-12 max-w-2xl mx-auto">
            Nous ne sommes pas une conciergerie classique. Nous sommes votre partenaire pour optimiser et valoriser votre bien.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Expertise locale', desc: 'Connaissance approfondie du marché suisse romand' },
              { icon: TrendingUp, title: 'Résultats mesurables', desc: 'Revenus optimisés et taux d\'occupation élevé' },
              { icon: MessageSquare, title: 'Relation de confiance', desc: 'Transparence totale et communication régulière' }
            ].map((item) => (
              <div key={item.title} className="text-center" data-testid={`why-us-item-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4 shadow-sm">
                  <item.icon className="h-8 w-8 text-[#8A9474]" />
                </div>
                <h3 className="font-semibold text-[#2B2B2B] mb-2 text-lg">{item.title}</h3>
                <p className="text-sm text-[#4A4A4A]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos Outils Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#2B2B2B] mb-6 text-center">
            Nos outils technologiques
          </h2>
          <p className="text-lg text-[#4A4A4A] mb-12 text-center max-w-2xl mx-auto">
            Nous utilisons les meilleurs outils pour gérer votre bien de manière optimale et professionnelle
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Loomy CRM */}
            <div className="bg-[#F5F3EF] p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-[#2B2B2B] mb-3">Loomy CRM</h3>
              <p className="text-[#4A4A4A] mb-4">
                Gestion centralisée de tous vos clients et réservations. Suivi en temps réel et reporting détaillé.
              </p>
              <a 
                href="https://www.loomy-crm.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#8A9474] hover:text-[#7A8464] font-medium inline-flex items-center gap-2"
              >
                En savoir plus <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            {/* Talk-Flow */}
            <div className="bg-[#F5F3EF] p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-[#2B2B2B] mb-3">Talk-Flow</h3>
              <p className="text-[#4A4A4A] mb-4">
                Gestion intelligente des appels, prise de rendez-vous automatisée et support 24/7 pour vos voyageurs.
              </p>
              <a 
                href="https://talk-flow.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#8A9474] hover:text-[#7A8464] font-medium inline-flex items-center gap-2"
              >
                En savoir plus <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            {/* Viralya */}
            <div className="bg-[#F5F3EF] p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-[#2B2B2B] mb-3">Viralya</h3>
              <p className="text-[#4A4A4A] mb-4">
                Maximisez votre visibilité sur les réseaux sociaux grâce à notre influenceur IA dédié.
              </p>
              <a 
                href="https://www.viralya.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#8A9474] hover:text-[#7A8464] font-medium inline-flex items-center gap-2"
              >
                En savoir plus <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Crédit Darven */}
          <div className="mt-12 text-center text-sm text-[#6B6B6B]">
            <p>Infrastructure digitale et acquisition client : <a href="https://darven.ch" target="_blank" rel="noopener noreferrer" className="text-[#8A9474] hover:text-[#7A8464] font-medium">Darven</a></p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#8A9474] to-[#7A8464] text-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold mb-6">
            Prêt à optimiser votre bien ?
          </h2>
          <p className="text-lg mb-8 opacity-90">
            Découvrez combien vous pourriez générer avec Homely Conciergerie
          </p>
          <Button 
            onClick={handleEstimateClick}
            size="lg"
            className="bg-white text-[#8A9474] hover:bg-gray-100 px-6 sm:px-10 py-4 sm:py-7 text-base sm:text-lg font-semibold shadow-xl"
            data-testid="services-cta-estimation"
          >
            Estimer mon bien <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </Layout>
  );
}
